// audio.js — browser mic capture (→ 16 kHz PCM16) and playback (24 kHz PCM16).
// Written to survive Safari, which refuses arbitrary AudioContext sample rates:
// we capture at the device's native rate and downsample by hand, and we let the
// AudioBufferSourceNode resample playback for us.

export function floatToPCM16(float32) {
  const out = new Int16Array(float32.length);
  for (let i = 0; i < float32.length; i++) {
    const s = Math.max(-1, Math.min(1, float32[i]));
    out[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
  }
  return out;
}

export function pcm16ToFloat(int16) {
  const out = new Float32Array(int16.length);
  for (let i = 0; i < int16.length; i++) out[i] = int16[i] / 32768;
  return out;
}

export function bytesToBase64(uint8) {
  let bin = '';
  const CHUNK = 0x8000;
  for (let i = 0; i < uint8.length; i += CHUNK) {
    bin += String.fromCharCode.apply(null, uint8.subarray(i, i + CHUNK));
  }
  return btoa(bin);
}

export function base64ToBytes(b64) {
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

// The microphone stays open during TTS for barge-in. Android must therefore
// remove its own speaker signal before frames reach provider VAD; Sarvam has no
// playback-reference/echo-cancellation parameter. Keep automatic gain off so a
// quiet residual echo is not amplified back into speech.
export const VOICE_CAPTURE_CONSTRAINTS = Object.freeze({
  channelCount: 1,
  echoCancellation: true,
  noiseSuppression: true,
  autoGainControl: false,
});

// Linear-interpolation downsample from inRate → outRate.
function downsample(float32, inRate, outRate) {
  if (outRate >= inRate) return float32;
  const ratio = inRate / outRate;
  const newLen = Math.round(float32.length / ratio);
  const out = new Float32Array(newLen);
  for (let i = 0; i < newLen; i++) {
    const idx = i * ratio;
    const i0 = Math.floor(idx);
    const i1 = Math.min(i0 + 1, float32.length - 1);
    const frac = idx - i0;
    out[i] = float32[i0] * (1 - frac) + float32[i1] * frac;
  }
  return out;
}

// The capture worklet (W4b): accumulates the worklet's 128-sample render
// quanta into the same 4096-sample blocks the old ScriptProcessorNode
// delivered, then posts each block to the main thread — so everything
// downstream (downsample → PCM16 → base64 → onChunk) is byte-identical to the
// deprecated path. Kept as a source string + Blob URL so the worklet ships
// inside this module (no separate asset to wire through Vite, the kiosk
// bundle, and the Capacitor shell).
const CAPTURE_WORKLET_SOURCE = `
class OpdCaptureProcessor extends AudioWorkletProcessor {
  constructor() { super(); this.buf = new Float32Array(4096); this.len = 0; }
  process(inputs) {
    const ch = inputs[0] && inputs[0][0];
    if (!ch) return true;
    let i = 0;
    while (i < ch.length) {
      const n = Math.min(ch.length - i, this.buf.length - this.len);
      this.buf.set(ch.subarray(i, i + n), this.len);
      this.len += n; i += n;
      if (this.len === this.buf.length) {
        this.port.postMessage(this.buf); // structured clone — buf is reusable
        this.len = 0;
      }
    }
    return true;
  }
}
registerProcessor('opd-capture', OpdCaptureProcessor);
`;

// Captures the mic and calls onChunk(base64Pcm16, approxMs) for each block.
// AudioWorklet-first (ScriptProcessorNode is deprecated and renders on the
// main thread, so a busy UI caused capture jank); the ScriptProcessor path
// survives as the documented fallback for the one real environment that
// lacks audioWorklet or blocks Blob worker modules via CSP — older clinic
// WebViews. Both paths produce identical 4096-sample blocks.
export class MicCapture {
  constructor(onChunk, { firstFrameTimeoutMs = 2_000 } = {}) {
    this.onChunk = onChunk;
    this.firstFrameTimeoutMs = firstFrameTimeoutMs;
  }
  async start() {
    try {
      this.stream = await navigator.mediaDevices.getUserMedia({
        audio: VOICE_CAPTURE_CONSTRAINTS,
      });
      this.ctx = new AudioContext(); // native rate (usually 44.1/48 kHz)
      this.src = this.ctx.createMediaStreamSource(this.stream);
      this.mute = this.ctx.createGain();
      this.mute.gain.value = 0; // route to destination silently so the graph is pulled
      let firstFrameResolve;
      const firstFrame = new Promise((resolve) => { firstFrameResolve = resolve; });
      const handleBlock = (float32) => {
        firstFrameResolve?.();
        firstFrameResolve = null;
        const ds = downsample(float32, this.ctx.sampleRate, 16000);
        const pcm = floatToPCM16(ds);
        const ms = (ds.length / 16000) * 1000;
        this.onChunk(bytesToBase64(new Uint8Array(pcm.buffer)), ms);
      };

      this.workletUrl = null;
      if (this.ctx.audioWorklet && typeof this.ctx.audioWorklet.addModule === 'function') {
        try {
          this.workletUrl = URL.createObjectURL(
            new Blob([CAPTURE_WORKLET_SOURCE], { type: 'application/javascript' }),
          );
          await this.ctx.audioWorklet.addModule(this.workletUrl);
          this.node = new AudioWorkletNode(this.ctx, 'opd-capture', {
            numberOfInputs: 1,
            numberOfOutputs: 1,
            channelCount: 1,
          });
          this.node.port.onmessage = (e) => handleBlock(e.data);
        } catch (_) {
          // CSP or WebView refused the Blob worklet module — fall through to
          // the ScriptProcessor path below. Capture still works either way.
          this.node = null;
        }
      }
      if (this.node) {
        this.src.connect(this.node);
        this.node.connect(this.mute);
      } else {
        this.proc = this.ctx.createScriptProcessor(4096, 1, 1);
        this.proc.onaudioprocess = (e) => handleBlock(e.inputBuffer.getChannelData(0));
        this.src.connect(this.proc);
        this.proc.connect(this.mute);
      }
      this.mute.connect(this.ctx.destination);
      if (this.ctx.state === 'suspended') await this.ctx.resume();
      if (this.ctx.state !== 'running') throw new Error(`microphone audio context is ${this.ctx.state}`);
      let timeout;
      await Promise.race([
        firstFrame,
        new Promise((_, reject) => {
          timeout = setTimeout(() => reject(new Error('microphone produced no audio frames')), this.firstFrameTimeoutMs);
        }),
      ]);
      clearTimeout(timeout);
    } catch (error) {
      this.stop();
      throw error;
    }
  }
  stop() {
    try { this.node && (this.node.port.onmessage = null); } catch {}
    try { this.proc && (this.proc.onaudioprocess = null); } catch {}
    try { this.src && this.src.disconnect(); } catch {}
    try { this.node && this.node.disconnect(); } catch {}
    try { this.proc && this.proc.disconnect(); } catch {}
    try { this.mute && this.mute.disconnect(); } catch {}
    try { this.stream && this.stream.getTracks().forEach((t) => t.stop()); } catch {}
    try { this.ctx && this.ctx.close(); } catch {}
    try { this.workletUrl && URL.revokeObjectURL(this.workletUrl); } catch {}
    this.node = null;
    this.proc = null;
    this.workletUrl = null;
  }
}

// Schedules 24 kHz PCM16 chunks back-to-back so playback doesn't crackle.
export class PCMPlayer {
  constructor() {
    this.queueTime = 0;
    this.sources = [];
    this.outRate = 24000;
  }
  ensure() {
    if (!this.ctx) {
      this.ctx = new AudioContext(); // native rate; buffers below carry their own rate
      this.queueTime = this.ctx.currentTime;
    }
  }
  play(int16) {
    this.ensure();
    const f32 = pcm16ToFloat(int16);
    const buf = this.ctx.createBuffer(1, f32.length, this.outRate); // source node resamples
    buf.copyToChannel(f32, 0);
    const node = this.ctx.createBufferSource();
    node.buffer = buf;
    node.connect(this.ctx.destination);
    const now = this.ctx.currentTime;
    if (this.queueTime < now) this.queueTime = now;
    node.start(this.queueTime);
    this.queueTime += buf.duration;
    this.sources.push(node);
    node.onended = () => { this.sources = this.sources.filter((s) => s !== node); };
  }
  // Stop everything queued (used when the model is interrupted / barged in on).
  interrupt() {
    this.sources.forEach((s) => { try { s.stop(); } catch {} });
    this.sources = [];
    if (this.ctx) this.queueTime = this.ctx.currentTime;
  }
  close() {
    this.interrupt();
    try { this.ctx && this.ctx.close(); } catch {}
    this.ctx = null;
  }
}
